import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { catchError, map, of } from 'rxjs'
import {
	ElectronicProcessPaginationRun,
	ElectronicProcessPaginationRunStatus,
	ElectronicProcessPaginationRunType,
	outdatedChangesLabel
} from '@models/planning/electronic-process-pagination.model'
import { ElectronicProcessPaginationService } from '@services/electronic-process-pagination.service'
import { AuthService } from '@services/auth.service'
import { AccountService } from '@services/account.service'

/**
 * Histórico de paginação do processo (spec 002, RF 23 e 24), no visual do histórico de publicações do PCA: uma entrada
 * por execução, da mais recente para a mais antiga, com tipo, quem pediu, quando, resultado e totais; ao expandir, as
 * mudanças de folha, as falhas e, na alteração que desatualizou a paginação, o que mudou.
 */
@Component({
	selector: 'app-electronic-process-pagination-history',
	templateUrl: './electronic-process-pagination-history.component.html',
	styleUrl: './electronic-process-pagination-history.component.scss',
	standalone: false
})
export class ElectronicProcessPaginationHistoryComponent implements OnChanges {
	@Input() processId: string | null = null

	/** Simulação pronta escolhida para revisar e aplicar. */
	@Output() review = new EventEmitter<ElectronicProcessPaginationRun>()

	runs: ElectronicProcessPaginationRun[] = []
	expanded = new Set<string>()
	loading = false
	page = 0
	hasMore = false
	authors: Record<string, string | null> = {}

	readonly pageSize = 10
	readonly Type = ElectronicProcessPaginationRunType

	constructor(
		private paginationService: ElectronicProcessPaginationService,
		private authService: AuthService,
		private accountService: AccountService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['processId']) this.reload()
	}

	/** Público: a tela chama quando uma execução termina. */
	reload() {
		this.page = 0
		this.runs = []
		this.expanded.clear()
		this.load()
	}

	loadMore() {
		this.page++
		this.load()
	}

	toggle(run: ElectronicProcessPaginationRun) {
		if (this.expanded.has(run.id)) this.expanded.delete(run.id)
		else this.expanded.add(run.id)
	}

	expandable(run: ElectronicProcessPaginationRun): boolean {
		return !!run.changes?.length || !!run.failures.length || !!run.details
	}

	/** Uma linha que resume o resultado, sem abrir os detalhes. */
	summary(run: ElectronicProcessPaginationRun): string | null {
		switch (run.type) {
			case ElectronicProcessPaginationRunType.OUTDATED:
				return `Mudou ${outdatedChangesLabel(run.details?.changed)} de "${run.details?.label ?? 'documento'}". A paginação ficou desatualizada.`

			case ElectronicProcessPaginationRunType.SIMULATION:
			case ElectronicProcessPaginationRunType.APPLY:
				if (run.status === ElectronicProcessPaginationRunStatus.COMPLETED && run.type === ElectronicProcessPaginationRunType.SIMULATION) {
					return 'Nenhuma folha muda.'
				}
				return run.changes?.length ? `${run.changes.length} arquivo(s) mudam de folha · ${this.totals(run)}` : this.totals(run)

			default:
				if (run.active) return run.progressLabel
				return run.pagesAfter != null ? this.totals(run) : null
		}
	}

	private totals(run: ElectronicProcessPaginationRun): string | null {
		if (run.pagesAfter == null) return null
		if (run.pagesBefore == null || run.pagesBefore === 0) return `${run.pagesAfter} folhas`
		return `Folhas: ${run.pagesBefore} → ${run.pagesAfter}`
	}

	private load() {
		if (!this.processId) return

		this.loading = true

		this.paginationService.history(this.processId, this.page, this.pageSize).subscribe({
			next: page => {
				const content = page?.content ?? []

				this.runs = this.page === 0 ? content : this.runs.concat(content)
				this.hasMore = !page?.last && content.length === this.pageSize
				this.loading = false

				// Uma falha recente já aparece aberta: é o que o usuário veio ver.
				if (this.page === 0 && this.runs[0]?.failed) this.expanded.add(this.runs[0].id)

				this.resolveAuthors(content)
			},
			error: () => {
				this.loading = false
				if (this.page === 0) this.runs = []
			}
		})
	}

	/** Nome de quem pediu ou alterou, quando o usuário pode consultar; sem permissão, a entrada fica sem autor. */
	private resolveAuthors(runs: ElectronicProcessPaginationRun[]) {
		const tenantId = this.accountService.tenantId

		new Set(runs.map(it => it.createdBy).filter(id => id && !(id in this.authors))).forEach(id => {
			this.authors[id] = null

			this.authService
				.detailUser(id, tenantId)
				.pipe(
					map(user => user?.name ?? null),
					catchError(() => of(null))
				)
				.subscribe(name => (this.authors[id] = name))
		})
	}
}
